import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Empty, Form, Input, Modal, Row, Space, Spin, Switch, Tabs, Tag, Typography, message } from "antd";
import { EditOutlined, FileTextOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";

import { scenarioContextApi } from "../../../../../api/scenarioContextApi";
import { useAuth } from "../../../../../hooks/useAuth";
import { getStandardCostTypeLabel } from "../../../constants";
import { PIPELINE_STAGES } from "../../pipeline";
import MarkdownViewEditor from "./MarkdownViewEditor";

const { Text } = Typography;

const PROMPT_ENTRY_TYPE = "PROMPT";

const PROMPT_AGENT_BY_STAGE_ID = {
    ocr: "ocr_agent",
    parser: "ap_invoice_parser_agent",
    allocation: "ap_cost_allocation_agent",
    validation: "ap_flight_cost_validator_agent",
    batch_validation: "ap_invoice_batch_validation_agent",
};

const PROMPT_TABS = PIPELINE_STAGES
    .filter((stage) => PROMPT_AGENT_BY_STAGE_ID[stage.stageId])
    .map((stage) => ({
        key: stage.stageId,
        label: stage.displayName,
        stageId: stage.stageId,
        agentName: PROMPT_AGENT_BY_STAGE_ID[stage.stageId],
    }));

const DEFAULT_PROMPT_TAB_KEY = PROMPT_TABS[0]?.key;

const normalizeText = (value) => String(value ?? "").trim();

const getErrorMessage = (error, fallbackMessage) => error.response?.data?.detail || error.response?.data?.message || error.message || fallbackMessage;

const getLabelValue = (labels = {}, keys = []) => {
    for (const key of keys) {
        if (labels[key]) return labels[key];
    }
    return undefined;
};

const PromptStagePane = ({ visible, stageConfig, predefinedSupplierId, predefinedCostType, hasRequiredContext, canCreate, canUpdate }) => {
    const [form] = Form.useForm();
    const [record, setRecord] = useState(null);
    const [mode, setMode] = useState("view");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const isEditing = mode === "edit";

    useEffect(() => {
        if (!visible) {
            setRecord(null);
            setMode("view");
            setLoading(false);
            setSaving(false);
            form.resetFields();
            return undefined;
        }

        if (!hasRequiredContext) {
            setRecord(null);
            setMode("view");
            setLoading(false);
            form.resetFields();
            return undefined;
        }

        let cancelled = false;
        const loadRecord = async () => {
            setLoading(true);
            try {
                const response = await scenarioContextApi.getList({
                    page: 1,
                    page_size: 1,
                    entry_type: PROMPT_ENTRY_TYPE,
                    agent_name: stageConfig.agentName,
                    scenario_supplier_id: predefinedSupplierId,
                    scenario_standard_cost_type: predefinedCostType,
                });

                if (cancelled) return;
                setRecord(response.data?.items?.[0] || null);
                setMode("view");
                form.resetFields();
            } catch (error) {
                if (!cancelled) message.error(getErrorMessage(error, `查询${stageConfig.label}提示词失败`));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadRecord();

        return () => {
            cancelled = true;
        };
    }, [form, hasRequiredContext, predefinedCostType, predefinedSupplierId, stageConfig.agentName, stageConfig.label, visible]);

    const openEditor = () => {
        if (!record && !canCreate) {
            message.error("您没有新增提示词的权限");
            return;
        }
        if (record && !canUpdate) {
            message.error("您没有编辑提示词的权限");
            return;
        }

        form.setFieldsValue({
            prompt_text: record?.prompt_text || "",
            description: record?.description || "",
            is_active: record?.is_active ?? true,
        });
        setMode("edit");
    };

    const cancelEditor = () => {
        form.resetFields();
        setMode("view");
    };

    const buildSubmitPayload = (values) => ({
        agent_name: stageConfig.agentName,
        entry_type: PROMPT_ENTRY_TYPE,
        scenario_conditions: {
            supplier_id: String(predefinedSupplierId),
            standard_cost_type: predefinedCostType,
        },
        prompt_text: String(values.prompt_text ?? ""),
        json_content: null,
        description: normalizeText(values.description),
        is_active: values.is_active !== false,
    });

    const handleSubmit = async () => {
        if (!hasRequiredContext) {
            message.error("缺少供应商或费用类型，无法保存提示词");
            return;
        }

        try {
            const values = await form.validateFields();
            if (!normalizeText(values.prompt_text)) {
                message.error("提示词内容不能为空");
                return;
            }

            const payload = buildSubmitPayload(values);
            setSaving(true);

            const response = record?.id
                ? await scenarioContextApi.update({ id: record.id, data: payload })
                : await scenarioContextApi.create(payload);

            if (response.data?.success === false) {
                throw new Error(response.data?.message || "保存提示词失败");
            }

            const savedRecord = response.data?.data?.scenario_context || { ...record, ...payload };
            message.success(response.data?.message || `${stageConfig.label}提示词已保存`);
            setRecord(savedRecord);
            setMode("view");
            form.resetFields();
        } catch (error) {
            if (error.errorFields) return;
            message.error(getErrorMessage(error, "保存提示词失败"));
        } finally {
            setSaving(false);
        }
    };

    const renderView = () => {
        if (!record) {
            return (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`该供应商该费用类型暂无${stageConfig.label}提示词配置`}>
                    <Button type="primary" icon={<PlusOutlined />} disabled={!hasRequiredContext || !canCreate} onClick={openEditor}>
                        创建提示词
                    </Button>
                </Empty>
            );
        }

        return (
            <Card
                title={`${stageConfig.label}提示词`}
                extra={(
                    <Space>
                        <Tag color={record.is_active ? "green" : "default"}>{record.is_active ? "启用" : "停用"}</Tag>
                        <Button type="primary" icon={<EditOutlined />} disabled={!canUpdate} onClick={openEditor}>
                            编辑
                        </Button>
                    </Space>
                )}
            >
                {record.description && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={record.description} />}
                <MarkdownViewEditor value={record.prompt_text || ""} readOnly minRows={18} emptyText="暂无提示词内容" />
            </Card>
        );
    };

    const renderEditor = () => (
        <Form form={form} layout="vertical">
            <Card size="small" title={record ? `编辑${stageConfig.label}提示词` : `创建${stageConfig.label}提示词`} style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item name="is_active" label="状态" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="description" label="描述说明">
                    <Input allowClear placeholder="可填写提示词说明" />
                </Form.Item>
            </Card>
            <Form.Item name="prompt_text" label="提示词内容">
                <MarkdownViewEditor
                    defaultMode="write"
                    placeholder="请输入提示词内容，支持 Markdown 格式"
                    emptyText="暂无提示词内容"
                    minRows={18}
                />
            </Form.Item>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Space>
                    <Button onClick={cancelEditor}>取消</Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
                        保存
                    </Button>
                </Space>
            </div>
        </Form>
    );

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "56px 0" }}>
                <Spin size="large" />
                <div style={{ marginTop: 12 }}>
                    <Text type="secondary">正在加载{stageConfig.label}提示词...</Text>
                </div>
            </div>
        );
    }

    return isEditing ? renderEditor() : renderView();
};

const PromptMaintenanceModal = ({ visible, onClose, predefinedFilters = {}, predefinedFilterLabels = {} }) => {
    const { hasPermission } = useAuth();
    const [activeTabKey, setActiveTabKey] = useState(DEFAULT_PROMPT_TAB_KEY);

    const predefinedSupplierId = predefinedFilters?.scenario_supplier_id;
    const predefinedCostType = predefinedFilters?.scenario_standard_cost_type;
    const hasRequiredContext = Boolean(predefinedSupplierId && predefinedCostType);
    const canCreate = hasPermission("agent:create");
    const canUpdate = hasPermission("agent:update");

    const fixedContext = useMemo(() => {
        const supplierLabel = getLabelValue(predefinedFilterLabels, [
            "scenario_supplier_label",
            "scenario_supplier_name",
            "supplierLabel",
            "supplierName",
            "supplier_name",
        ]) || predefinedSupplierId || "-";
        const costTypeLabel = getLabelValue(predefinedFilterLabels, [
            "scenario_standard_cost_type_label",
            "standardCostTypeLabel",
            "standard_cost_type_label",
            "costTypeLabel",
        ]) || getStandardCostTypeLabel(predefinedCostType);

        return { supplierLabel, costTypeLabel };
    }, [predefinedCostType, predefinedFilterLabels, predefinedSupplierId]);

    useEffect(() => {
        if (!visible) {
            setActiveTabKey(DEFAULT_PROMPT_TAB_KEY);
        }
    }, [visible]);

    const tabItems = PROMPT_TABS.map((stageConfig) => ({
        key: stageConfig.key,
        label: stageConfig.label,
        children: (
            <PromptStagePane
                visible={visible}
                stageConfig={stageConfig}
                predefinedSupplierId={predefinedSupplierId}
                predefinedCostType={predefinedCostType}
                hasRequiredContext={hasRequiredContext}
                canCreate={canCreate}
                canUpdate={canUpdate}
            />
        ),
    }));

    const renderFixedContext = () => (
        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 16px" }}>
            <Row gutter={[16, 8]}>
                <Col xs={24} md={12}>
                    <Text type="secondary">供应商</Text>
                    <div style={{ marginTop: 4 }}>
                        <Tag color="blue">{fixedContext.supplierLabel}</Tag>
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <Text type="secondary">费用类型</Text>
                    <div style={{ marginTop: 4 }}>
                        <Tag color="orange">{fixedContext.costTypeLabel}</Tag>
                    </div>
                </Col>
            </Row>
        </Card>
    );

    return (
        <Modal
            title={(
                <Space>
                    <FileTextOutlined style={{ color: "#1677ff" }} />
                    <span>提示词维护</span>
                </Space>
            )}
            open={visible}
            onCancel={onClose}
            width={1080}
            className="fullscreen-modal"
            destroyOnHidden
            footer={[
                <Button key="close" onClick={onClose}>
                    关闭
                </Button>,
            ]}
            styles={{ body: { maxHeight: "calc(100vh - 140px)", overflowY: "auto", padding: "16px 24px" } }}
        >
            {renderFixedContext()}
            {!hasRequiredContext && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="缺少固定上下文"
                    description="提示词维护需要当前供应商和费用类型，当前入口未提供完整预设条件。"
                />
            )}
            <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} items={tabItems} />
        </Modal>
    );
};

export default PromptMaintenanceModal;
